import { forwardRef, useEffect, useMemo, useState } from "react";
import { submitBasicMedicalInfo } from "../../api/forms/medical-info";
import { BasicMedicalInfo } from "../../api/forms/medical-info/types";
import { Nullable } from "../../api/types";
import useDataInputForm, {
    booleanOptions,
    FORM_IDS,
} from "../../hooks/useDataInputForm";
import useImperativeFormSubmit from "../../hooks/useImperativeFormSubmit";
import { FormHandle, InputTypes } from "../../types";
import { onFormValuesChange } from "../../utils";
import Error from "../Error";
import DataInputGenericForm, {
    DataInputGenericFormProps,
} from "./DataInputGenericForm";

export default forwardRef<FormHandle<Nullable<BasicMedicalInfo>> | null, {}>(
    function BasicMedicalInfoForm(
        props,
        ref: React.ForwardedRef<FormHandle<Nullable<BasicMedicalInfo>> | null>
    ) {
        const { formData, loading, error, setFromData } = useDataInputForm({
            formId: FORM_IDS.MEDICAL_INFO,
        });

        const [formValues, setFormValues] = useState<
            BasicMedicalInfo | null | undefined
        >();

        useEffect(() => {
            if (formData) {
                setFormValues(formData as BasicMedicalInfo);
            }
        }, [formData]);

        const { valuesErrors, submissionLoading } =
            useImperativeFormSubmit<BasicMedicalInfo>({
                formValues,
                formData,
                ref,
                onSubmitSuccess: setFromData,
                submitFunction: submitBasicMedicalInfo,
            });

        type FormFields =
            DataInputGenericFormProps<BasicMedicalInfo>["formFields"];

        const formFields = useMemo<FormFields>(
            (): FormFields =>
                !formData || !formValues
                    ? []
                    : [
                          {
                              id: "antiarrhythmicDrugs",
                              label: "Antiarrhythmic Drugs",
                              value: formValues.antiarrhythmicDrugs,
                              valueToDisplay: formValues.antiarrhythmicDrugs,
                              originalValue: formData.antiarrhythmicDrugs,
                              type: InputTypes.SELECT,
                              error: valuesErrors?.antiarrhythmicDrugs,
                              options: booleanOptions,
                              required: false,
                              isSmallInput: true,
                          },
                          {
                              id: "currentIllness",
                              label: "Underlying Condition",
                              inputClassName: "w-20 xl:w-32",
                              value: formValues.currentIllness,
                              valueToDisplay:
                                  formValues.currentIllness.join(", "),
                              originalValue: formData.currentIllness,
                              type: InputTypes.SELECT,
                              options: formData.allCurrentIllness,
                              multiple: true,
                              error: valuesErrors?.currentIllness,
                              required: false,
                          },
                      ],
            [formData, formValues, valuesErrors]
        );

        if (error) {
            return <Error />;
        }

        return (
            <DataInputGenericForm
                formTitle="Medical Information"
                formFields={formFields}
                formData={formValues}
                onFromValuesChange={({ value, fieldSettings }) =>
                    onFormValuesChange({
                        value,
                        fieldSettings,
                        setFormValues,
                    })
                }
                loading={loading || submissionLoading}
            />
        );
    }
);
